// useCountdown.ts — 计算「距 TTL 截止还剩多久」的单秒倒数。
//
// 用途:配对请求 10 分钟 TTL 显示「剩余 X 分 Y 秒」倒计时,接近过期变红警示。
// iso 支持响应式来源 (Ref/Getter):起点变化时自动重置计时 — 允许同一组件
// 先以 undefined 挂载、后再接到真实请求 (如配对详情弹窗)。

import { onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue'

export interface CountdownApi {
  /** 剩余毫秒数 (≤ 0 表示已过期) */
  remainingMs: Ref<number>
  /** 形如 "9:42" 或 "0:07" 的简短标签;0 时返回 "0:00" */
  remainingLabel: Ref<string>
  /** 0..1 的剩余比例 (相对 ttlMs),便于做进度条 */
  remainingRatio: Ref<number>
  /** 是否进入「紧急」区间 (默认 < 60s) */
  urgent: Ref<boolean>
  /** 是否已过期 (含 iso 未就绪的情况) */
  expired: Ref<boolean>
}

export interface CountdownOptions {
  /** 阈值 (毫秒)。低于此值 urgent=true。默认 60000。 */
  urgentMs?: number
  /** 是否在组件 unmount 时自动停止 tick。默认 true。 */
  autoStop?: boolean
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return '0:00'
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/**
 * @param iso 起始时间 ISO 字符串 (例如后端 PairingRequest.at) — 可为 Ref/Getter
 * @param ttlMs 距起始时间的有效期 (毫秒)。默认 600_000 (10 分钟)
 * @param opts urgent 阈值 / 自动停止选项
 */
export function useCountdown(
  iso: MaybeRefOrGetter<string | undefined>,
  ttlMs: MaybeRefOrGetter<number> = 600_000,
  opts: CountdownOptions = {},
): CountdownApi {
  const urgentMs = opts.urgentMs ?? 60_000
  const autoStop = opts.autoStop ?? true

  const remainingMs = ref(0)
  const remainingLabel = ref('0:00')
  const remainingRatio = ref(0)
  const urgent = ref(false)
  const expired = ref(true)

  let timer: ReturnType<typeof setInterval> | null = null

  function stopTimer(): void {
    if (timer !== null) {
      clearInterval(timer)
      timer = null
    }
  }

  function startTimer(): void {
    if (timer !== null) return
    timer = setInterval(tick, 1000)
    tick()
  }

  function tick(): void {
    const isoVal = toValue(iso)
    const ttlVal = toValue(ttlMs)
    const start = isoVal ? Date.parse(isoVal) : NaN
    if (!Number.isFinite(start)) {
      // iso 未就绪 — 显示过期态并暂停 tick (iso 变化时 watch 会重启)
      remainingMs.value = 0
      remainingLabel.value = '0:00'
      remainingRatio.value = 0
      urgent.value = false
      expired.value = true
      stopTimer()
      return
    }
    const ms = Math.max(0, ttlVal - (Date.now() - start))
    remainingMs.value = ms
    remainingLabel.value = formatRemaining(ms)
    remainingRatio.value = Math.max(0, Math.min(1, ms / ttlVal))
    urgent.value = ms > 0 && ms < urgentMs
    expired.value = ms <= 0
    if (ms <= 0) stopTimer()
  }

  // 起点变化 (切换请求 / 数据到达) → 重置计时
  watch(
    () => toValue(iso),
    () => {
      stopTimer()
      startTimer()
    },
  )

  startTimer()

  if (autoStop) {
    onBeforeUnmount(stopTimer)
  }

  return { remainingMs, remainingLabel, remainingRatio, urgent, expired }
}
